import { Info } from '@phosphor-icons/react';

import { HeroProgressBar } from '../common/HeroProgressBar';
import { CardErrorState, ERROR_BOX_CLASS } from '../common/CardErrorState';
import {
  TooltipContent,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
} from '../ui/Tooltip';
import pt from '../../locales/pt';
import { formatEUR } from '../../utils/money';
import {
  cycleSavingsRatePct,
  cycleSpendableRemainingEur,
  cycleTotalSpendableEur,
} from '../../utils/cycleFinancialMetrics';
import { cycleDaysRemaining } from '../../utils/cycleDays';
import { cn } from '../../utils/cn';

/**
 * Z3 — `.pen` → `ds/card/cycle-summary` (`xYxY1`).
 *
 * Deixou de ser um cartão em gradiente: é agora um cartão de superfície
 * (`$surface-card` / `$border` / `$radius-ds-lg`, `padding $space-5`,
 * `gap $space-4`) com três blocos:
 *
 * 1. `head` (`gap $space-1`): `eyebrow-row` («Ainda podes gastar»
 *    `$type-body-sm` + info 14, `pb $space-1`) · readout `$type-display-kpi` ·
 *    subtítulo `$type-body-sm`
 * 2. `bar-block` (`gap $space-2`): barra 6px `$track`/`$sage-500` · escala
 *    (% disponível / ritmo diário) em `$type-label`
 * 3. `foot` (`pt $space-4`, traço superior `$border`): «TAXA DE POUPANÇA»
 *    `$type-section` + info, e o valor `$type-display-stat` à direita
 *
 * O master ganhou também um bloco `error` (altura 200) com o mesmo cartão.
 * (Alertas vivem na secção própria `AlertsPanel`.)
 */
export function DashboardCycleSummary({
  overview,
  monthlySummary,
  cycle,
  isError = false,
  onRetry,
  className,
}) {
  const remainingRaw = cycleSpendableRemainingEur(overview);
  const hasRemaining = remainingRaw !== null && overview?.has_income;
  const remaining = hasRemaining ? remainingRaw : null;

  const totalSpendable = cycleTotalSpendableEur(overview);

  const fillPct =
    totalSpendable > 0 && remaining != null
      ? Math.min(100, Math.max(0, (remaining / totalSpendable) * 100))
      : 0;

  const daysLeft = cycleDaysRemaining(cycle);
  const savingsPct = cycleSavingsRatePct(monthlySummary);
  const dailyRate = remaining != null && daysLeft > 0 ? remaining / daysLeft : null;

  const cardClass = cn(
    'flex w-full min-w-0 flex-col gap-space-4 rounded-ds-lg border border-border bg-card p-space-5',
    className,
  );

  if (isError) {
    /** `.pen` → `error`: mesmo cartão, conteúdo centrado. */
    return (
      <div className={cn(cardClass, ERROR_BOX_CLASS)}>
        <CardErrorState message={pt.common.cycleLoadError} onRetry={onRetry} />
      </div>
    );
  }

  return (
    <div className={cardClass}>
      {/* `.pen` › `head` */}
      <div className="flex min-w-0 flex-col gap-space-1">
        <div className="flex min-w-0 items-center gap-space-2 pb-space-1 text-muted-foreground">
          <p className="type-body-sm min-w-0 flex-1">{pt.dashboard.cycleSummary.remaining}</p>
          <TooltipProvider delayDuration={200}>
            <TooltipRoot>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="shrink-0 text-muted-foreground hover:text-foreground"
                  aria-label={pt.dashboard.cycleSummary.spendableTooltip}
                >
                  <Info className="h-3.5 w-3.5" aria-hidden />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">
                {pt.dashboard.cycleSummary.spendableTooltip}
              </TooltipContent>
            </TooltipRoot>
          </TooltipProvider>
        </div>

        <p className="type-display-kpi text-foreground">
          {remaining != null ? formatEUR(remaining) : '—'}
        </p>

        {totalSpendable > 0 && remaining != null ? (
          <p className="type-body-sm text-muted-foreground">
            {pt.dashboard.cycleSummary.ofBudget.replace('{amount}', formatEUR(totalSpendable))}
          </p>
        ) : null}
      </div>

      {/* `.pen` › `bar-block` */}
      {totalSpendable > 0 && remaining != null ? (
        <div className="flex min-w-0 flex-col gap-space-2">
          <HeroProgressBar
            variant="surface"
            percentage={fillPct}
            ariaLabel={pt.dashboard.cycleSummary.remaining}
          />
          <div className="flex items-center justify-between gap-space-2 text-muted-foreground">
            <span className="type-label-meta">
              {pt.dashboard.cycleSummary.pctAvailable.replace('{pct}', fillPct.toFixed(0))}
            </span>
            {dailyRate != null ? (
              <span className="type-label-meta text-right">
                {pt.dashboard.cycleSummary.spendRate.replace('{rate}', formatEUR(dailyRate))}
              </span>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* `.pen` › `foot` */}
      {savingsPct != null ? (
        <div className="flex min-w-0 items-center justify-between gap-space-3 border-t border-border pt-space-4">
          <div className="flex min-w-0 flex-1 items-center gap-space-1">
            <p className="type-section text-muted-foreground">
              {pt.dashboard.cycleSummary.savingsRate}
            </p>
            <TooltipProvider delayDuration={200}>
              <TooltipRoot>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="shrink-0 text-muted-foreground hover:text-foreground"
                    aria-label={pt.dashboard.cycleSummary.savingsRateTooltip}
                  >
                    <Info className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {pt.dashboard.cycleSummary.savingsRateTooltip}
                </TooltipContent>
              </TooltipRoot>
            </TooltipProvider>
          </div>
          <p className="type-display-stat shrink-0 text-foreground">
            {savingsPct.toFixed(0)}%
          </p>
        </div>
      ) : null}
    </div>
  );
}

export default DashboardCycleSummary;
